import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  MutableRefObject,
} from 'react';
import { LexicalEditor } from 'lexical';
import { Annotation } from '../../../TextAnnotation/types';

import {
  useActiveProject,
  useActiveProjectCohortId,
  useActiveUser,
  useLocalStorageState,
  useMixpanel,
  useService,
} from '../../../../hooks';
import { useNotify } from '../../../../hooks/useNotify';
import { useCarePlans } from '../../../../hooks/useCarePlans';
import { CarePlanService } from '../../../../services/CarePlanService';
import { ClinicalImpressionService } from '../../../../services/ClinicalImpressionService';
import { ViewMode } from '../AITools/Attachments/InlineAttachment';
import { ExecutiveSummaryData } from './executive-summary';
import { findLatestActionPlan, orderImpressionTypes } from './utils';

import { AxiosError, AxiosResponse } from 'axios';
import { useAsync, useDebounce } from 'react-use';
import { defineMessages, useIntl } from 'react-intl';

import { KnownExtraSections } from '../../../AITemplates/ImpressionTypeFilter';
import { ActionPlanActivity } from '../ActionPlan/action-plan';
import {
  ImpressionItem,
  KnownImpressionTypes,
} from '../AITools/ImpressionEditor/ImpressionItem';
import { EXECUTIVE_SUMMARY_IMPRESSION_TYPES } from './constants';
import { upsertReviewBaseFingerprintExtension } from '../../../../util/contentFingerprint';

const svc = new ClinicalImpressionService();
const actionPlanService = new CarePlanService();

const deleteImpressionMessages = defineMessages({
  conflict: {
    id: 'executiveSummaryContext.deleteImpressionConflict',
    defaultMessage:
      'This report was updated elsewhere. We loaded the latest version—try again if you still want to remove it.',
  },
  failure: {
    id: 'executiveSummaryContext.deleteImpressionFailure',
    defaultMessage: 'Could not remove the report. Please try again.',
  },
});

const saveCarePlanMessages = defineMessages({
  conflict: {
    id: 'executiveSummaryContext.saveCarePlanConflict',
    defaultMessage:
      'This action plan was updated elsewhere. We loaded the latest version—reapply your changes if needed.',
  },
  tooLarge: {
    id: 'executiveSummaryContext.saveCarePlanTooLarge',
    defaultMessage:
      'This action plan is too large to save. Remove or shorten some items, then try again.',
  },
  failure: {
    id: 'executiveSummaryContext.saveCarePlanFailure',
    defaultMessage: 'Could not save the action plan. Please try again.',
  },
});

/**
 * When saving a `ClinicalImpression` whose `summary` in the payload is not the
 * server narrative you read (RFC 1881 pin), pass the **stored** summary string here.
 * If omitted, `impression.summary` is used as the fingerprint base.
 */
export type SaveClinicalImpressionFingerprintOptions = {
  baseSummaryForFingerprint?: string;
};

// Define the shape of the context data - split into 3 contexts

// Data Context - Rarely changes, heavy data operations
export type ExecutiveSummaryDataContextType = {
  // Executive Summary State
  patient: fhir.Patient;
  executiveSummary: ExecutiveSummaryData | null;
  enabledSummaries?: string[];
  effectiveSummaries: string[];
  isDirty: boolean;

  // Derived Data
  enabledImpressionTypes: (KnownImpressionTypes | KnownExtraSections)[];
  enabledImpressionItems: ImpressionItem[];

  // Loading and Saving State
  isSaving: boolean;
  isRefetching: boolean;
  isLoading: boolean;
  saveImpression: (
    impression: fhir.ClinicalImpression,
    options?: SaveClinicalImpressionFingerprintOptions,
  ) => Promise<AxiosResponse<fhir.ClinicalImpression>>;

  // Care Plan Management
  fetchCarePlan: () => void;
  carePlans: fhir.CarePlan[];
  onChangeCarePlan: (carePlanId: string) => void;

  // State Management Functions
  setExecutiveSummary: (executiveSummary: ExecutiveSummaryData | null) => void;
  setDuration: (start?: Date, end?: Date) => void;
  setStatus: (status: string) => void;
  setVersion: (version: string) => void;
  removeActivity: (activity: fhir.CarePlanActivity) => void;
  toggleActionItemStatus: (activity: fhir.CarePlanActivity) => void;
  addActionItem: (activity: fhir.CarePlanActivity) => void;
  updateActionItem: (
    previousActivity: fhir.CarePlanActivity,
    activity: fhir.CarePlanActivity,
  ) => void;
  setLinkExtension: (
    account: string | undefined,
    projectId: string,
    dashboardId: string,
  ) => void;
  setKeyFindings: (keyFindings: string[]) => void;
  deleteImpression: (impressionId: string) => Promise<boolean>;
  removeImpression: (impressionId: string) => void;
  forceUpdate: () => void;
  setPristine: () => void;
  refetchImpression: (impressionId: string) => Promise<void>;
};

// Selection Context - Changes moderately when user navigates
export type ExecutiveSummarySelectionContextType = {
  // Impression Management
  impressionTypeFilter: KnownImpressionTypes | KnownExtraSections;
  setImpressionTypeFilter: (
    value: KnownImpressionTypes | KnownExtraSections,
  ) => void;
  activeImpression: ImpressionItem | undefined;
  setActiveImpression: React.Dispatch<
    React.SetStateAction<ImpressionItem | undefined>
  >;

  // Action Item Management
  activeActionItem: ActionPlanActivity | undefined;
  setActiveActionItem: React.Dispatch<
    React.SetStateAction<ActionPlanActivity | undefined>
  >;
  selectedActionItem: fhir.CarePlanActivity | undefined;
  setSelectedActionItem: React.Dispatch<
    React.SetStateAction<fhir.CarePlanActivity | undefined>
  >;

  // View Mode
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
};

// UI Context - Changes frequently (modals, editing state)
export type ExecutiveSummaryUIContextType = {
  // Editing State
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;

  // Modal State
  isPresentationModalOpen: boolean;
  setIsPresentationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showFeedbackModal: boolean;
  setShowFeedbackModal: React.Dispatch<React.SetStateAction<boolean>>;
  isActionItemModalOpen: boolean;
  setIsActionItemModalOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Sidebar State
  isSidebarVisible: boolean;
  setSidebarVisible: (visible: boolean) => void;
};

const addMissingActivityKeyFindingsToExecutiveSummary = (
  executiveSummary: ExecutiveSummaryData,
  activity: fhir.CarePlanActivity,
) => {
  const newKeyFindings =
    activity.detail?.reasonReference?.map(r => r.display) ?? [];
  const existingKeyFindings = executiveSummary
    .getKeyFindings()
    .map(kf => kf.toLowerCase());
  const keyFindingsToAdd: string[] = newKeyFindings?.filter(
    kf => !!kf && !existingKeyFindings.includes(kf.toLowerCase()),
  ) as string[];
  if (keyFindingsToAdd.length) {
    executiveSummary.setKeyFindings(
      Array.from(
        new Set([...executiveSummary.getKeyFindings(), ...keyFindingsToAdd]),
      ),
    );
  }
};

// Create three separate contexts
const ExecutiveSummaryDataContext = createContext<
  ExecutiveSummaryDataContextType | undefined
>(undefined);

const ExecutiveSummarySelectionContext = createContext<
  ExecutiveSummarySelectionContextType | undefined
>(undefined);

const ExecutiveSummaryUIContext = createContext<
  ExecutiveSummaryUIContextType | undefined
>(undefined);

type GenerateDrawerContextType = {
  isDrawerOpen: boolean;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  annotations: Annotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<Annotation[]>>;
  editorRef: MutableRefObject<LexicalEditor | null>;
  onImpressionCleanupRef: MutableRefObject<(() => void) | null>;
  isSuggesting: boolean;
  setIsSuggesting: React.Dispatch<React.SetStateAction<boolean>>;
};

const GenerateDrawerContext = createContext<
  GenerateDrawerContextType | undefined
>(undefined);

export const ExecutiveSummaryProvider: React.FC<{
  patient: fhir.Patient;

  /** Allows a context to only show specific summaries (AI Tools uses this) */
  enabledSummaries?: KnownImpressionTypes[];

  /** When true, will not cache (or use cached) care plan id from selector (Action Plan uses this) */
  forceLatestCarePlan?: boolean;
  controlledCarePlanId?: string;
  onCarePlanIdChange?: (carePlanId: string) => void;
  controlledImpressionTypeFilter?: KnownImpressionTypes | KnownExtraSections;
  onImpressionTypeFilterChange?: (
    value: KnownImpressionTypes | KnownExtraSections,
  ) => void;
}> = ({
  children,
  enabledSummaries,
  patient,
  forceLatestCarePlan,
  controlledCarePlanId,
  onCarePlanIdChange,
  controlledImpressionTypeFilter,
  onImpressionTypeFilterChange,
}) => {
  const patientId = patient.id as string;
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isActionItemModalOpen, setIsActionItemModalOpen] = useState(false);

  const [storedImpressionTypeFilter, setStoredImpressionTypeFilter] =
    useLocalStorageState<KnownImpressionTypes | KnownExtraSections>(
      `es-impression-type-filter-${patientId}`,
      'overview',
    );

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [activeImpression, setActiveImpression] = useState<
    ImpressionItem | undefined
  >(undefined);

  const [isSidebarVisible, setSidebarVisible] = useLocalStorageState<boolean>(
    `es-sidebar-visible`,
    false,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const editorRef = useRef<LexicalEditor | null>(null);
  const onImpressionCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    onImpressionCleanupRef.current?.();
    setAnnotations([]);
    setIsDrawerOpen(false);
  }, [activeImpression?.id]);

  const [viewMode, setViewMode] = useLocalStorageState<ViewMode>(
    `es-view-mode-${patientId}`,
    'expanded',
  );

  const [isPresentationModalOpen, setIsPresentationModalOpen] = useState(
    viewMode === 'presentation-expanded' || viewMode === 'presentation-compact',
  );

  const [effectiveSummaries] = useState<KnownImpressionTypes[]>(
    enabledSummaries || [...EXECUTIVE_SUMMARY_IMPRESSION_TYPES],
  );

  // State variables (no longer using reducer)
  const [executiveSummary, setExecutiveSummaryState] =
    useState<ExecutiveSummaryData | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const carePlanState = useCarePlans(patientId);
  const [project] = useActiveProject();
  const cohort = useActiveProjectCohortId();
  const [listImpressions] = useService(svc.listImpressions);
  const [saveImpression] = useService(svc.saveImpression, {
    rethrowErrors: true,
  });
  const [saveExecutiveSummary, saveExecutiveSummaryMeta] = useService(
    actionPlanService.saveCarePlan,
    { rethrowErrors: true },
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [notify] = useNotify();
  const { formatMessage } = useIntl();

  const [carePlanIdState, setCarePlanIdState] = useState<string | undefined>(
    forceLatestCarePlan
      ? undefined
      : localStorage.getItem(`${patientId}-carePlanId`) || undefined,
  );

  const impressionTypeFilter =
    controlledImpressionTypeFilter ?? storedImpressionTypeFilter;
  const setImpressionTypeFilter = useCallback(
    (value: KnownImpressionTypes | KnownExtraSections) => {
      onImpressionTypeFilterChange?.(value);
      if (controlledImpressionTypeFilter === undefined) {
        setStoredImpressionTypeFilter(value);
      }
    },
    [
      controlledImpressionTypeFilter,
      onImpressionTypeFilterChange,
      setStoredImpressionTypeFilter,
    ],
  );

  const carePlanId = controlledCarePlanId ?? carePlanIdState;

  useEffect(() => {
    if (controlledCarePlanId !== undefined) {
      setIsRefetching(true);
    }
  }, [controlledCarePlanId]);

  // Tracks whether the pending debounced save represents a transition into
  // `completed` status, so `updateExecutiveSummary` can emit the sign-off
  // outcome at the confirmed persistence boundary rather than on click.
  const pendingSignOffRef = useRef(false);

  // State management functions (simplified without multi-instance support)
  const setExecutiveSummary = useCallback(
    (newExecutiveSummary: ExecutiveSummaryData | null) => {
      setExecutiveSummaryState(newExecutiveSummary);
      setIsDirty(false);
      // Discarding dirty local state also discards a pending sign-off; otherwise
      // a later unrelated save could emit a false annual_report_sign_off_completed.
      pendingSignOffRef.current = false;
    },
    [],
  );

  const setDuration = useCallback(
    (start?: Date, end?: Date) => {
      if (!executiveSummary) return;

      const currentDuration = executiveSummary.getDuration();
      executiveSummary.setDuration({
        start: start || currentDuration.start,
        end: end || currentDuration.end,
      });
      setExecutiveSummaryState(executiveSummary.copy());
      setIsDirty(true);
    },
    [executiveSummary],
  );

  const setStatus = useCallback(
    (status: string) => {
      if (!executiveSummary) return;

      pendingSignOffRef.current =
        status === 'completed' && executiveSummary.status !== 'completed';
      executiveSummary.setStatus(status);
      setExecutiveSummaryState(executiveSummary.copy());
      setIsDirty(true);
    },
    [executiveSummary],
  );

  const setVersion = useCallback(
    (version: string) => {
      if (!executiveSummary) return;

      executiveSummary.setVersion(version);
      setExecutiveSummaryState(executiveSummary.copy());
      // Setting version is not a dirty update
    },
    [executiveSummary],
  );

  const removeActivity = useCallback(
    (activity: fhir.CarePlanActivity) => {
      if (!executiveSummary) return;

      executiveSummary.removeActionItem(activity);
      setExecutiveSummaryState(executiveSummary.copy());
      setIsDirty(true);
    },
    [executiveSummary],
  );

  const toggleActionItemStatus = useCallback(
    (activity: fhir.CarePlanActivity) => {
      if (!executiveSummary) return;

      executiveSummary.toggleActionItemStatus(activity);
      setExecutiveSummaryState(executiveSummary.copy());
      setIsDirty(true);
    },
    [executiveSummary],
  );

  const addActionItem = useCallback(
    (activity: fhir.CarePlanActivity) => {
      if (!executiveSummary) return;

      executiveSummary.addActionItem(activity);
      addMissingActivityKeyFindingsToExecutiveSummary(
        executiveSummary,
        activity,
      );
      setExecutiveSummaryState(executiveSummary.copy());
      setIsDirty(true);
    },
    [executiveSummary],
  );

  const updateActionItem = useCallback(
    (
      previousActivity: fhir.CarePlanActivity,
      activity: fhir.CarePlanActivity,
    ) => {
      if (!executiveSummary) return;

      executiveSummary.updateActionItem(previousActivity, activity);
      addMissingActivityKeyFindingsToExecutiveSummary(
        executiveSummary,
        activity,
      );
      setExecutiveSummaryState(executiveSummary.copy());
      setIsDirty(true);
    },
    [executiveSummary],
  );

  const [activeActionItem, setActiveActionItem] = useState<
    ActionPlanActivity | undefined
  >(undefined);
  const [selectedActionItem, setSelectedActionItem] = useState<
    fhir.CarePlanActivity | undefined
  >(undefined);

  const setLinkExtension = useCallback(
    (account: string | undefined, projectId: string, dashboardId: string) => {
      if (!executiveSummary) return;

      executiveSummary.setLinkExtension({ account, projectId, dashboardId });
      setExecutiveSummaryState(executiveSummary.copy());
      setIsDirty(true);
    },
    [executiveSummary],
  );

  const setKeyFindings = useCallback(
    (keyFindings: string[]) => {
      if (!executiveSummary) return;

      executiveSummary.setKeyFindings(keyFindings);
      setExecutiveSummaryState(executiveSummary.copy());
      setIsDirty(true);
    },
    [executiveSummary],
  );

  const removeImpression = useCallback(
    (impressionId: string) => {
      if (!executiveSummary) return;

      executiveSummary.removeImpression(impressionId);
      setExecutiveSummaryState(executiveSummary.copy());
      setIsDirty(true);
    },
    [executiveSummary],
  );

  const forceUpdate = useCallback(() => {
    if (!executiveSummary) return;

    setExecutiveSummaryState(executiveSummary.copy());
  }, [executiveSummary]);

  const setPristine = useCallback(() => {
    setIsDirty(false);
  }, []);

  const onChangeCarePlan = useCallback(
    (carePlanId: string) => {
      if (forceLatestCarePlan) {
        return;
      }
      // store the last selected care plan id
      localStorage.setItem(`${patientId}-carePlanId`, carePlanId);
      setIsRefetching(true);
      onCarePlanIdChange?.(carePlanId);
      if (controlledCarePlanId === undefined) {
        setCarePlanIdState(carePlanId);
      }
    },
    [controlledCarePlanId, forceLatestCarePlan, onCarePlanIdChange, patientId],
  );

  const saveImpressionChanges = useCallback(
    async (
      impression: fhir.ClinicalImpression,
      options?: SaveClinicalImpressionFingerprintOptions,
    ) => {
      setIsSaving(true);
      try {
        const baseSummary =
          options?.baseSummaryForFingerprint !== undefined
            ? options.baseSummaryForFingerprint
            : typeof impression.summary === 'string'
            ? impression.summary
            : '';
        const impressionWithFingerprint = {
          ...impression,
          extension: await upsertReviewBaseFingerprintExtension(
            impression.extension,
            baseSummary,
          ),
        };
        return await saveImpression({
          patientId,
          projectId: project.id,
          impressionId: impression.id!,
          impression: impressionWithFingerprint,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [patientId, project.id, saveImpression],
  );

  const { loading: isLoading } = useAsync(async () => {
    if (
      !carePlanState.carePlans?.length ||
      (executiveSummary && !isRefetching)
    ) {
      return;
    }

    // An Action Plan with no impressions is still this record. Select by
    // standard category (useCarePlans) plus Active-first ordering — do not
    // require supportingInfo. Annual Report empty state keys off impressions.
    const carePlan =
      (carePlanId &&
        carePlanState.carePlans.find(cp => cp.id === carePlanId)) ||
      findLatestActionPlan(carePlanState.carePlans);

    if (!carePlan) {
      return;
    }

    const ids = carePlan.supportingInfo
      ?.map(supportingInfo => supportingInfo.reference?.split('/')[1])
      .join(',');
    if (!ids) {
      setIsRefetching(false);
      setExecutiveSummary(new ExecutiveSummaryData([], carePlan));
      return;
    }
    const impressions = (
      await listImpressions({
        patientId,
        cohort,
        projectId: project.id,
        _id: ids,
      })
    ).data.entry?.map(entry => entry.resource as fhir.ClinicalImpression);
    setIsRefetching(false);
    setExecutiveSummary(new ExecutiveSummaryData(impressions ?? [], carePlan));
  }, [
    executiveSummary,
    carePlanState.carePlans,
    listImpressions,
    project.id,
    patientId,
    cohort,
    setExecutiveSummary,
    carePlanId,
  ]);

  const fetchCarePlan = useCallback(() => {
    // forceLatestCarePlan skips onChangeCarePlan (no cached id). Still mark
    // refetching so an already-loaded summary — including a completed plan
    // shown while there is no Active — is replaced after Start or Generate.
    if (forceLatestCarePlan) {
      setIsRefetching(true);
    }
    carePlanState.refetchCarePlans();
    carePlanId && onChangeCarePlan(carePlanId);
  }, [carePlanState, carePlanId, onChangeCarePlan, forceLatestCarePlan]);

  const deleteImpression = useCallback(
    async (impressionId: string): Promise<boolean> => {
      const impression = executiveSummary?.impressions.find(
        impression => impression.id === impressionId,
      );
      if (!impression) {
        return false;
      }
      const baseSummaryForFingerprint =
        typeof impression.impression.summary === 'string'
          ? impression.impression.summary
          : '';
      try {
        await saveImpressionChanges(
          {
            ...impression.impression,
            status: 'entered-in-error', // not-done or stopped might be more appropriate, but are only valid in r5 types
          },
          { baseSummaryForFingerprint },
        );
        removeImpression(impressionId);
        return true;
      } catch (err) {
        const isConflict =
          err instanceof AxiosError && err.response?.status === 409;
        notify(
          formatMessage(
            isConflict
              ? deleteImpressionMessages.conflict
              : deleteImpressionMessages.failure,
          ),
          'error',
        );
        if (isConflict) {
          fetchCarePlan();
        }
        return false;
      }
    },
    [
      executiveSummary,
      saveImpressionChanges,
      removeImpression,
      notify,
      formatMessage,
      fetchCarePlan,
    ],
  );

  const refetchImpression = useCallback(
    async (impressionId: string) => {
      if (!executiveSummary) return;
      const result = await listImpressions({
        patientId,
        cohort,
        projectId: project.id,
        _id: impressionId,
      });
      const updated = result.data.entry?.[0]
        ?.resource as fhir.ClinicalImpression;
      if (!updated) return;

      const idx = executiveSummary.impressions.findIndex(
        i => i.id === impressionId,
      );
      if (idx === -1) return;

      const newItem = new ImpressionItem(updated);
      executiveSummary.impressions[idx] = newItem;
      setExecutiveSummaryState(executiveSummary.copy());

      setActiveImpression(prev => (prev?.id === impressionId ? newItem : prev));
    },
    [executiveSummary, listImpressions, patientId, cohort, project.id],
  );

  const authorId = useActiveUser()?.id;
  const mixpanel = useMixpanel();
  const pendingUpdate = useRef<ExecutiveSummaryData>();
  const updateExecutiveSummary = useCallback(async () => {
    if (!executiveSummary || !executiveSummary.carePlan.id || !isDirty) return;
    if (saveExecutiveSummaryMeta.isFetching) {
      return (pendingUpdate.current = executiveSummary);
    }
    setPristine();
    executiveSummary.setLastEditedBy(authorId);
    const isSignOff = pendingSignOffRef.current;
    pendingSignOffRef.current = false;
    try {
      const newPlan = await saveExecutiveSummary(
        executiveSummary.carePlan.id,
        executiveSummary.carePlan,
        cohort,
      );
      mixpanel.track('action_plan_saved', { outcome: 'success' });
      if (isSignOff) {
        mixpanel.track('annual_report_sign_off_completed', {
          outcome: 'success',
        });
      }
      if (newPlan?.data?.meta?.versionId) {
        setVersion(newPlan.data.meta.versionId);
      }
      if (pendingUpdate.current) {
        pendingUpdate.current = undefined;
        setIsDirty(true);
        forceUpdate();
      }
      carePlanState.refetchCarePlans();
    } catch (err) {
      mixpanel.track('action_plan_saved', { outcome: 'error' });
      if (isSignOff) {
        mixpanel.track('annual_report_sign_off_completed', {
          outcome: 'error',
        });
        pendingSignOffRef.current = true;
      }
      setIsDirty(true);
      const status =
        err instanceof AxiosError ? err.response?.status : undefined;
      const message =
        status === 409
          ? saveCarePlanMessages.conflict
          : status === 413
          ? saveCarePlanMessages.tooLarge
          : saveCarePlanMessages.failure;
      notify(formatMessage(message), 'error');
      if (status === 409) {
        fetchCarePlan();
      }
    }
  }, [
    saveExecutiveSummaryMeta,
    saveExecutiveSummary,
    executiveSummary,
    isDirty,
    cohort,
    setPristine,
    setVersion,
    forceUpdate,
    authorId,
    carePlanState,
    mixpanel,
    notify,
    formatMessage,
    fetchCarePlan,
  ]);

  useDebounce(updateExecutiveSummary, 1000, [executiveSummary]);

  const enabledImpressionTypes = useMemo(() => {
    const esSummaryTypes =
      executiveSummary?.impressions.map(i => i.impressionType) || [];
    if (!effectiveSummaries) {
      return esSummaryTypes;
    }
    const enabledImpressionTypes = esSummaryTypes.filter(type =>
      effectiveSummaries.includes(type),
    );
    // keep the order of impressions in executive summary v2 (using crd)
    if (executiveSummary?.compositeReportDefinitionDurableId)
      return enabledImpressionTypes;
    return orderImpressionTypes(enabledImpressionTypes);
  }, [executiveSummary, effectiveSummaries]);

  const enabledImpressionItems = useMemo<ImpressionItem[]>(
    () =>
      executiveSummary?.impressions.filter(i =>
        enabledImpressionTypes.includes(i.impressionType),
      ) || [],
    [executiveSummary, enabledImpressionTypes],
  );

  useEffect(() => {
    if (!executiveSummary || !enabledImpressionItems?.length) return;

    const hasSelection =
      impressionTypeFilter === 'actionItems' ||
      impressionTypeFilter === 'labResults' ||
      enabledImpressionItems?.some(
        impression => impression.impressionType === impressionTypeFilter,
      );
    if (!hasSelection) {
      setActiveImpression(enabledImpressionItems?.[0]);
      setImpressionTypeFilter(
        enabledImpressionItems?.[0]?.impressionType ?? 'actionItems',
      );
    } else if (
      hasSelection &&
      impressionTypeFilter !== 'actionItems' &&
      impressionTypeFilter !== 'labResults'
    ) {
      setActiveImpression(
        enabledImpressionItems?.find(
          impression => impression.impressionType === impressionTypeFilter,
        ),
      );
    }
  }, [
    executiveSummary,
    enabledImpressionItems,
    enabledImpressionItems?.length,
    impressionTypeFilter,
    setImpressionTypeFilter,
    setActiveImpression,
  ]);

  // Memoize each context value separately to prevent unnecessary re-renders
  const dataContextValue = useMemo<ExecutiveSummaryDataContextType>(
    () => ({
      patient,
      executiveSummary,
      enabledSummaries,
      effectiveSummaries,
      isDirty,
      enabledImpressionTypes,
      enabledImpressionItems,
      isSaving,
      isRefetching,
      isLoading: isLoading || carePlanState.loading,
      saveImpression: saveImpressionChanges,
      fetchCarePlan,
      carePlans: carePlanState.carePlans || [],
      onChangeCarePlan,
      setExecutiveSummary,
      setDuration,
      setStatus,
      setVersion,
      removeActivity,
      toggleActionItemStatus,
      addActionItem,
      updateActionItem,
      setLinkExtension,
      setKeyFindings,
      deleteImpression,
      removeImpression,
      forceUpdate,
      setPristine,
      refetchImpression,
    }),
    [
      patient,
      executiveSummary,
      enabledSummaries,
      effectiveSummaries,
      isDirty,
      enabledImpressionTypes,
      enabledImpressionItems,
      isSaving,
      isRefetching,
      isLoading,
      carePlanState,
      saveImpressionChanges,
      fetchCarePlan,
      onChangeCarePlan,
      setExecutiveSummary,
      setDuration,
      setStatus,
      setVersion,
      removeActivity,
      toggleActionItemStatus,
      addActionItem,
      updateActionItem,
      setLinkExtension,
      setKeyFindings,
      deleteImpression,
      removeImpression,
      refetchImpression,
      forceUpdate,
      setPristine,
    ],
  );

  const selectionContextValue = useMemo<ExecutiveSummarySelectionContextType>(
    () => ({
      impressionTypeFilter,
      setImpressionTypeFilter,
      activeImpression,
      setActiveImpression,
      activeActionItem,
      setActiveActionItem,
      selectedActionItem,
      setSelectedActionItem,
      viewMode,
      setViewMode,
    }),
    [
      impressionTypeFilter,
      setImpressionTypeFilter,
      activeImpression,
      activeActionItem,
      selectedActionItem,
      viewMode,
      setViewMode,
    ],
  );

  const uiContextValue = useMemo<ExecutiveSummaryUIContextType>(
    () => ({
      isEditing,
      setIsEditing,
      isPresentationModalOpen,
      setIsPresentationModalOpen,
      showFeedbackModal,
      setShowFeedbackModal,
      isActionItemModalOpen,
      setIsActionItemModalOpen,
      isSidebarVisible,
      setSidebarVisible,
    }),
    [
      isEditing,
      isPresentationModalOpen,
      showFeedbackModal,
      isActionItemModalOpen,
      isSidebarVisible,
      setSidebarVisible,
    ],
  );

  const generateDrawerContextValue = useMemo<GenerateDrawerContextType>(
    () => ({
      isDrawerOpen,
      setIsDrawerOpen,
      annotations,
      setAnnotations,
      editorRef,
      onImpressionCleanupRef,
      isSuggesting,
      setIsSuggesting,
    }),
    [isDrawerOpen, annotations, isSuggesting],
  );

  return (
    <ExecutiveSummaryDataContext.Provider value={dataContextValue}>
      <ExecutiveSummarySelectionContext.Provider value={selectionContextValue}>
        <ExecutiveSummaryUIContext.Provider value={uiContextValue}>
          <GenerateDrawerContext.Provider value={generateDrawerContextValue}>
            {children}
          </GenerateDrawerContext.Provider>
        </ExecutiveSummaryUIContext.Provider>
      </ExecutiveSummarySelectionContext.Provider>
    </ExecutiveSummaryDataContext.Provider>
  );
};

// Custom hooks to use each specific context
export const useExecutiveSummaryData = (): ExecutiveSummaryDataContextType => {
  const context = useContext(ExecutiveSummaryDataContext);
  if (!context) {
    throw new Error(
      'useExecutiveSummaryData must be used within an ExecutiveSummaryProvider',
    );
  }
  return context;
};

export const useExecutiveSummarySelection =
  (): ExecutiveSummarySelectionContextType => {
    const context = useContext(ExecutiveSummarySelectionContext);
    if (!context) {
      throw new Error(
        'useExecutiveSummarySelection must be used within an ExecutiveSummaryProvider',
      );
    }
    return context;
  };

export const useExecutiveSummaryUI = (): ExecutiveSummaryUIContextType => {
  const context = useContext(ExecutiveSummaryUIContext);
  if (!context) {
    throw new Error(
      'useExecutiveSummaryUI must be used within an ExecutiveSummaryProvider',
    );
  }
  return context;
};

export const useGenerateDrawer = (): GenerateDrawerContextType => {
  const context = useContext(GenerateDrawerContext);
  if (!context) {
    throw new Error(
      'useGenerateDrawer must be used within an ExecutiveSummaryProvider',
    );
  }
  return context;
};
